<script setup lang="ts">
import { OWidget, OWidgetKPIValue } from '@ai/ui-components'
import { computed, ref, watch, onMounted } from 'vue'
import { useColdMonitoringApi } from '@/composables/api/useColdMonitoringApi'
import { useHomeDateRange } from '@/composables/home/useHomeDateRange'
import { useAgentsRealtime } from '@/composables/realtime/useAgentsRealtime'
import type { ColdMonitoringKPI } from 'shared'
import {
  toWidgetDurationFromMinutes,
  toWidgetDurationFromSeconds,
  type WidgetDuration,
} from '@/utils/widget-duration'

const emit = defineEmits<(e: 'error', error: Error) => void>()

const { t } = useI18n({ useScope: 'global' })

// Real-time data from WebSocket
const { agentsList, isLoading: realtimeLoading, applyFilters } = useAgentsRealtime()

// Trigger initial WebSocket data fetch
onMounted(() => {
  applyFilters({})
})

const ongoingCallsCount = computed(() =>
  agentsList.value.reduce((total, agent) => total + agent.activeCalls.length, 0),
)

// Get default date range from composable if not provided via props
const dateRange = useHomeDateRange()

// API setup
const { fetchColdStats } = useColdMonitoringApi({
  period: ref(undefined),
  agents: ref(undefined),
})

const coldStats = ref<ColdMonitoringKPI[] | null>(null)
const error = ref<Error | null>(null)
const loading = ref(false)

// Fetch cold monitoring stats
const fetchStats = async () => {
  try {
    error.value = null
    loading.value = true
    const result = await fetchColdStats({
      datetimeFrom: dateRange.value.datetimeFrom,
      datetimeTo: dateRange.value.datetimeTo,
    })

    coldStats.value = result.kpis
  } catch (err) {
    error.value = err instanceof Error ? err : new Error('Failed to fetch cold stats')
    emit('error', error.value)
  } finally {
    loading.value = false
  }
}

watch(
  dateRange,
  (newVal, oldVal) => {
    if (
      oldVal?.datetimeFrom !== newVal.datetimeFrom ||
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      oldVal?.datetimeTo !== newVal.datetimeTo
    ) {
      void fetchStats()
    }
  },
  { immediate: true },
)

interface HomeKpiItem {
  id: string
  title: string
  value?: number | string | null
  duration?: WidgetDuration | null
  loading: boolean
}

const stats = computed<HomeKpiItem[]>(() => {
  const avgDuration = coldStats.value?.find((kpi) => kpi.id === 'avg-duration')
  const totalMinutes = coldStats.value?.find((kpi) => kpi.id === 'total-minutes')
  const totalColdCalls = coldStats.value?.find((kpi) => kpi.id === 'total-calls')
  const totalLiveCalls = realtimeLoading.value ? 0 : ongoingCallsCount.value

  return [
    {
      id: 'avg-duration',
      title: t('cold_monitoring.kpi.average_calling_duration'),
      duration: avgDuration?.value ? toWidgetDurationFromSeconds(avgDuration.value) : null,
      loading: loading.value,
    },
    {
      id: 'total-calling-duration',
      title: t('cold_monitoring.kpi.total_calling_duration'),
      duration: totalMinutes?.value ? toWidgetDurationFromMinutes(totalMinutes.value) : null,
      loading: loading.value,
    },
    {
      id: 'total-calls',
      title: t('home.kpi.total_calls'),
      value: (totalColdCalls?.value ?? 0) + totalLiveCalls || null,
      loading: loading.value || realtimeLoading.value,
    },
    {
      id: 'active-calls',
      title: t('home.kpi.active_calls'),
      value: realtimeLoading.value ? null : ongoingCallsCount.value || null,
      loading: realtimeLoading.value,
    },
  ]
})
</script>

<template>
  <div class="grid gap-4 grid-cols-[repeat(auto-fit,minmax(300px,1fr))]">
    <OWidget v-for="kpi in stats" :key="kpi.id" :title="kpi.title" icon="phone" :expandable="false">
      <template v-if="kpi.loading">
        <OSpinner size="S" />
      </template>
      <template #kpi>
        <OWidgetKPIValue v-if="!kpi.loading" :value="kpi.value" :duration="kpi.duration" />
      </template>
    </OWidget>
  </div>
</template>
